import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SystemHeader from '../SystemHeader';
import { F1API } from '../../services/feature-api';

const PAGE_SIZE = 30;

// Tiền hiển thị theo ĐƠN VỊ TRIỆU, 1 chữ số thập phân (vd 1,3 tỷ -> "1.300", 705.4tr -> "705,4").
// KHÔNG đuôi "tr"/"tỷ" — toàn bảng đồng nhất đơn vị triệu.
const fmtTrieu = (n) => {
  const v = Number(n) || 0;
  if (!v) return '-';
  return (v / 1e6).toLocaleString('vi-VN', { maximumFractionDigits: 1 });
};

// Tiền đầy đủ theo ĐỒNG — dùng trong popup chi tiết (KHÔNG làm tròn).
const fmtVnd = (n) => `${(Number(n) || 0).toLocaleString('vi-VN')}đ`;

const fmtInt = (n) => {
  const v = Number(n) || 0;
  return v ? v.toLocaleString('vi-VN') : '-';
};

// Cell color cho matrix doanh số nhập 12 tháng:
// xanh = đã duyệt (approved), vàng = chờ duyệt (pending), đỏ = bị từ chối (rejected), xám = chưa nộp.
const cellStatusStyle = (status, hasAmount) => {
  if (status === 'approved') return 'bg-green-100 text-green-800 hover:bg-green-200';
  if (status === 'rejected') return 'bg-red-100 text-red-700 hover:bg-red-200';
  if (status === 'pending') return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200';
  if (hasAmount) return 'bg-gray-50 text-gray-600 hover:bg-gray-100';
  return 'text-gray-300';
};

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const F1List = ({ accessToken, userInfo, onLogout, totalPoints, onRefreshPoints }) => {
  const navigate = useNavigate();
  const tableScrollRef = useRef(null);

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  // totals = BE cộng TOÀN BỘ F1 khớp bộ lọc (không phải chỉ 30 dòng của trang) → hàng TỔNG dưới header.
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [selectedInventoryF1, setSelectedInventoryF1] = useState(null);
  const [selectedF2F1, setSelectedF2F1] = useState(null);
  // saleDetail dùng chung cho 2 popup: kind='nhap' (sale_reports, click ô năm) và kind='ban' (order_details, click DS Bán).
  const [saleDetail, setSaleDetail] = useState(null);
  const [impersonatingId, setImpersonatingId] = useState(null);

  const now = new Date();
  // selectedMonth điều khiển kỳ lọc: F1+, Lẻ/Sỉ SP, DS Bán, Thi đua đổi theo. Ma trận năm KHÔNG đổi theo tháng.
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear] = useState(now.getFullYear());

  const searchTimer = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(val);
      setPage(1);
    }, 400);
  };

  const totalPages = Math.ceil(count / PAGE_SIZE);

  const loadData = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError('');
    try {
      const result = await F1API.getList(accessToken, {
        page,
        pageSize: PAGE_SIZE,
        search: debouncedSearch,
        month: selectedMonth,
        year: selectedYear,
      });
      if (result.success) {
        setRows(result.rows || []);
        setCount(result.count || 0);
        setTotals(result.totals || null);
      } else {
        setError(result.error || 'Lỗi tải dữ liệu');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [accessToken, page, debouncedSearch, selectedMonth, selectedYear]);

  useEffect(() => { loadData(); }, [loadData]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getFullYear()).slice(2)}`;
  };

  // Smart scroll: tap nút → table tự scroll-left đến anchor cột tương ứng.
  // Bù 130px cho cột "Thành viên" sticky-left khỏi che mất nhóm vừa nhảy đến.
  const scrollToAnchor = (anchorId) => {
    const el = document.getElementById(anchorId);
    if (!el || !tableScrollRef.current) return;
    const scroll = tableScrollRef.current;
    const scrollRect = scroll.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const targetLeft = scroll.scrollLeft + elRect.left - scrollRect.left - 130;
    scroll.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' });
  };

  // Popup DOANH SỐ NHẬP (sale_reports) — click ô T{m} trong ma trận năm.
  const openMonthDetail = async (row, month) => {
    setSaleDetail({ kind: 'nhap', row, month, loading: true, items: [], total: 0, status: row[`m${month}Status`] });
    try {
      const result = await F1API.getSaleDetails(accessToken, row.id, month, selectedYear);
      if (result.success && !result.data?.denied) {
        setSaleDetail({
          kind: 'nhap', row, month,
          items: result.data?.items || [],
          total: result.data?.total || 0,
          status: row[`m${month}Status`],
          submission: result.data?.submission || null,
        });
      } else {
        setSaleDetail({ kind: 'nhap', row, month, items: [], total: 0, status: row[`m${month}Status`], denied: result.data?.denied });
      }
    } catch (e) {
      setSaleDetail({ kind: 'nhap', row, month, items: [], total: 0, error: e.message });
    }
  };

  // Popup DOANH SỐ BÁN (order_details) — click cột "DS Bán", theo THÁNG đang lọc.
  const openSellDetail = async (row) => {
    setSaleDetail({ kind: 'ban', row, month: selectedMonth, loading: true, items: [], total: 0 });
    try {
      const result = await F1API.getSellDetails(accessToken, row.id, selectedMonth, selectedYear);
      if (result.success && !result.data?.denied) {
        // Chuẩn hoá order_details về cùng shape với popup nhập: {productName, count, price, amount}.
        const items = (result.data?.items || []).map((it, i) => ({
          id: i,
          productName: it.productName,
          count: it.quantity,
          price: it.unitPrice,
          amount: it.subtotal,
          type: it.type, // 1=lẻ, 2=sỉ → modal tách 2 khu vực
        }));
        setSaleDetail({ kind: 'ban', row, month: selectedMonth, items, total: result.data?.total || 0 });
      } else {
        setSaleDetail({ kind: 'ban', row, month: selectedMonth, items: [], total: 0, denied: result.data?.denied });
      }
    } catch (e) {
      setSaleDetail({ kind: 'ban', row, month: selectedMonth, items: [], total: 0, error: e.message });
    }
  };

  // "Xem trang F1 của người này": lấy token 15' rồi điều hướng /auto-login (imp=1) → đăng nhập TẠM THỜI dưới F1 → /f1 của họ.
  // Cùng tab; phiên impersonate lưu sessionStorage nên KHÔNG kẹt lần sau + có banner "Quay về tài khoản gốc".
  const impersonateF1 = async (row) => {
    if (impersonatingId) return;
    setImpersonatingId(row.id);
    try {
      const result = await F1API.getImpersonateToken(accessToken, row.id);
      const token = result?.data?.token;
      if (!token) {
        setError(result?.data?.denied ? 'Không có quyền xem tài khoản F1 này.' : (result?.error || 'Không tạo được phiên xem.'));
        setImpersonatingId(null);
        return;
      }
      window.location.href = `/auto-login?token=${encodeURIComponent(token)}&redirect=${encodeURIComponent('/f1')}&imp=1`;
    } catch (e) {
      setError(e.message);
      setImpersonatingId(null);
    }
  };

  // Ma trận doanh số nhập: hiện T1 → tháng hiện tại (tháng tương lai chắc chắn chưa có data nên ẩn).
  const maxMonth = selectedYear < now.getFullYear() ? 12 : (selectedYear > now.getFullYear() ? 0 : now.getMonth() + 1);
  const displayMonths = MONTHS.filter(m => m <= maxMonth);

  // Tổng số cột (cho colSpan skeleton/empty): Thành viên + Quân số(3) + Kho(1) + Thi đua(7) + Doanh số bán(3) + Doanh số nhập năm(1 + N).
  const totalCols = 1 + 3 + 1 + 7 + 3 + 1 + displayMonths.length;

  // Hàng TỔNG: ưu tiên totals của BE (cộng toàn bộ F1 khớp bộ lọc). BE cũ chưa trả totals thì cộng tạm
  // các dòng đang hiện — nhãn ô đầu ghi rõ "trang này" để không hiểu nhầm là tổng của tất cả.
  const SUM_KEYS = ['f2Count', 'tongF', 'newF1Count', 'totalInventory', 'dailyCount', 'meetingApproved',
    'reelsApproved', 'trendApproved', 'liveApproved', 'marketApproved', 'spaApproved',
    'retailOrders', 'wholesaleOrders', 'dsBan', 'totalSalesYear', ...MONTHS.map(m => `m${m}Amount`)];
  const sumRows = (k) => rows.reduce((s, r) => {
    const v = k === 'dsBan'
      ? (r.dsBan ?? ((Number(r.retailAmount) || 0) + (Number(r.wholesaleAmount) || 0)))
      : r[k];
    return s + (Number(v) || 0);
  }, 0);
  const totalsView = totals || SUM_KEYS.reduce((acc, k) => { acc[k] = sumRows(k); return acc; }, {});
  const thT = 'bg-amber-100 border border-gray-300 border-y-2 border-y-amber-500 px-1 py-1 text-center font-black text-amber-950';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SystemHeader
        userInfo={userInfo}
        onLogout={onLogout}
        totalPoints={totalPoints}
        pageTitle="Danh sách F1"
        onRefreshPoints={onRefreshPoints}
      />

      <div className="flex-1 max-w-full w-full flex flex-col">
        {/* Top bar */}
        <div className="flex items-center gap-3 px-3 py-2 bg-white border-b border-gray-200">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors text-blue-700 bg-blue-50 hover:bg-blue-100"
          >
            <span className="material-symbols-rounded" style={{ fontSize: '14px' }}>arrow_back</span>
            <span>Quay lại</span>
          </button>
          <h1 className="text-sm font-semibold text-blue-900 flex-1">Quản lý F1</h1>
        </div>

        {/* Bộ lọc tháng — pill T1..T12, mặc định tháng hiện tại */}
        <div className="px-2 py-1.5 flex items-center gap-1 overflow-x-auto bg-white border-b border-gray-200">
          <span className="flex-shrink-0 text-[11px] font-semibold text-gray-500 mr-0.5">Tháng</span>
          {MONTHS.map(m => (
            <button key={m} onClick={() => { setSelectedMonth(m); setPage(1); }}
                    className={`flex-shrink-0 w-8 h-7 text-[11px] font-bold rounded-md border transition-colors ${
                      selectedMonth === m
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                    }`}>
              T{m}
            </button>
          ))}
        </div>

        {/* Smart-scroll button bar */}
        <div className="px-2 py-1.5 flex gap-1 overflow-x-auto bg-white border-b border-gray-200">
          <button onClick={() => scrollToAnchor('anchor-quanso')}
                  className="flex-shrink-0 px-2.5 py-1 text-[11px] font-medium rounded-md bg-slate-100 border border-slate-200 text-slate-700 hover:bg-slate-200">
            ⓘ Quân số
          </button>
          <button onClick={() => scrollToAnchor('anchor-thidua')}
                  className="flex-shrink-0 px-2.5 py-1 text-[11px] font-medium rounded-md bg-violet-100 border border-violet-200 text-violet-700 hover:bg-violet-200">
            ★ Thi đua
          </button>
          <button onClick={() => scrollToAnchor('anchor-doanhso-ban')}
                  className="flex-shrink-0 px-2.5 py-1 text-[11px] font-medium rounded-md bg-green-100 border border-green-200 text-green-800 hover:bg-green-200">
            Doanh số (bán + nhập) (triệu)
          </button>
        </div>

        {/* Search */}
        <div className="px-2 py-1.5">
          <div className="relative">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 material-symbols-rounded text-gray-400" style={{ fontSize: '16px' }}>search</span>
            <input
              type="text"
              value={search}
              onChange={handleSearchChange}
              placeholder="Tìm theo mã, tên, facebook..."
              className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
            {search && (
              <button onClick={() => { setSearch(''); setDebouncedSearch(''); setPage(1); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <span className="material-symbols-rounded" style={{ fontSize: '16px' }}>close</span>
              </button>
            )}
          </div>
        </div>

        {/* Count + pagination */}
        <div className="px-3 pb-1 flex items-center justify-between text-[11px] text-gray-500">
          <span>{loading ? 'Đang tải...' : `${count} thành viên`}{debouncedSearch ? ` (tìm kiếm)` : ''}</span>
          {totalPages > 1 && <span>Trang {page}/{totalPages}</span>}
        </div>

        {error && (
          <div className="mx-2 mb-2 p-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg">{error}</div>
        )}

        {/* Bảng — scroll ngang, nhóm có màu, sticky-left "Thành viên" */}
        <div ref={tableScrollRef} className="flex-1 overflow-x-auto overflow-y-auto bg-white border-t border-b border-gray-200 relative">
          <table className="text-[11px] border-collapse" style={{ borderSpacing: 0 }}>
            <thead className="sticky top-0 z-30">
              {/* Row 0: group header — thứ tự: Quân số -> Kho -> Thi đua tháng -> Doanh số bán -> Doanh số nhập năm */}
              <tr>
                <th rowSpan={2} className="sticky left-0 z-40 bg-white border border-gray-300 px-2 py-1 text-left font-semibold text-gray-800 min-w-[120px]">
                  Thành viên
                </th>
                <th id="anchor-quanso" colSpan={3}
                    className="bg-slate-200 border border-gray-300 px-1.5 py-1 text-center font-semibold text-slate-800">
                  Quân số
                </th>
                <th colSpan={1}
                    className="bg-orange-200 border border-gray-300 px-1.5 py-1 text-center font-semibold text-orange-900">
                  Kho
                </th>
                <th id="anchor-thidua" colSpan={7}
                    className="bg-violet-200 border border-gray-300 px-1.5 py-1 text-center font-semibold text-violet-900">
                  Thi đua tháng
                </th>
                <th id="anchor-doanhso-ban" colSpan={3}
                    className="bg-green-200 border border-gray-300 px-2 py-1 text-left font-semibold text-green-900">
                  Doanh số bán (tháng)
                </th>
                <th id="anchor-doanhso-nam" colSpan={1 + displayMonths.length}
                    className="bg-yellow-200 border border-gray-300 px-2 py-1 text-left font-semibold text-yellow-900">
                  Doanh số nhập năm {selectedYear} (triệu)
                </th>
              </tr>
              {/* Row 1: column headers */}
              <tr>
                {/* Quân số */}
                <th className="bg-slate-50 border border-gray-300 px-1 py-0.5 text-center font-semibold text-slate-700 min-w-[44px]">F1</th>
                <th className="bg-slate-50 border border-gray-300 px-1 py-0.5 text-center font-semibold text-slate-700 min-w-[52px]">Tổng F</th>
                <th className="bg-slate-50 border border-gray-300 px-1 py-0.5 text-center font-semibold text-slate-700 min-w-[44px]">F1+</th>
                {/* Kho */}
                <th className="bg-orange-50 border border-gray-300 px-1 py-0.5 text-center font-semibold text-orange-800 min-w-[56px]">Kho SP</th>
                {/* Thi đua tháng */}
                <th className="bg-violet-50 border border-gray-300 px-1 py-0.5 text-center font-semibold text-violet-800 min-w-[40px]">BC</th>
                <th className="bg-violet-50 border border-gray-300 px-1 py-0.5 text-center font-semibold text-violet-800 min-w-[40px]">Họp</th>
                <th className="bg-violet-50 border border-gray-300 px-1 py-0.5 text-center font-semibold text-violet-800 min-w-[44px]">Reels</th>
                <th className="bg-violet-50 border border-gray-300 px-1 py-0.5 text-center font-semibold text-violet-800 min-w-[44px]">Trend</th>
                <th className="bg-violet-50 border border-gray-300 px-1 py-0.5 text-center font-semibold text-violet-800 min-w-[40px]">Live</th>
                <th className="bg-violet-50 border border-gray-300 px-1 py-0.5 text-center font-semibold text-violet-800 min-w-[36px]">Tt</th>
                <th className="bg-violet-50 border border-gray-300 px-1 py-0.5 text-center font-semibold text-violet-800 min-w-[36px]">Spa</th>
                {/* Doanh số bán (tháng) */}
                <th className="bg-green-50 border border-gray-300 px-1 py-0.5 text-center font-semibold text-green-800 min-w-[52px]">Lẻ SP</th>
                <th className="bg-green-50 border border-gray-300 px-1 py-0.5 text-center font-semibold text-green-800 min-w-[52px]">Sỉ SP</th>
                <th className="bg-green-50 border border-gray-300 px-1 py-0.5 text-center font-semibold text-green-900 min-w-[64px]">DS Bán</th>
                {/* Doanh số nhập năm — Tổng năm lên ĐẦU, rồi T{m} chỉ tháng có data */}
                <th className="bg-yellow-100 border border-gray-300 px-1 py-0.5 text-center font-semibold text-yellow-900 min-w-[72px]">Tổng nhập</th>
                {displayMonths.map(m => (
                  <th key={m} className="bg-yellow-50 border border-gray-300 px-1 py-0.5 text-center font-semibold text-yellow-800 min-w-[60px]">T{m}</th>
                ))}
              </tr>
              {/* Row 2: HÀNG TỔNG — nền vàng đậm, chữ to hơn hàng thường để tách hẳn khỏi dữ liệu từng người */}
              <tr className="text-[12px]">
                <th className="sticky left-0 z-40 bg-amber-200 border border-gray-300 border-y-2 border-y-amber-500 px-2 py-1 text-left font-black text-amber-950 min-w-[120px]">
                  TỔNG
                  <span className="block text-[10px] font-semibold text-amber-800">
                    {totals ? `${count.toLocaleString('vi-VN')} thành viên` : 'trang này'}
                  </span>
                </th>
                <th className={thT}>{fmtInt(totalsView.f2Count)}</th>
                <th className={thT}>{fmtInt(totalsView.tongF)}</th>
                <th className={thT}>{totalsView.newF1Count > 0 ? `+${fmtInt(totalsView.newF1Count)}` : '-'}</th>
                <th className={thT}>{fmtInt(totalsView.totalInventory)}</th>
                <th className={thT}>{fmtInt(totalsView.dailyCount)}</th>
                <th className={thT}>{fmtInt(totalsView.meetingApproved)}</th>
                <th className={thT}>{fmtInt(totalsView.reelsApproved)}</th>
                <th className={thT}>{fmtInt(totalsView.trendApproved)}</th>
                <th className={thT}>{fmtInt(totalsView.liveApproved)}</th>
                <th className={thT}>{fmtInt(totalsView.marketApproved)}</th>
                <th className={thT}>{fmtInt(totalsView.spaApproved)}</th>
                <th className={thT}>{fmtInt(totalsView.retailOrders)}</th>
                <th className={thT}>{fmtInt(totalsView.wholesaleOrders)}</th>
                <th className={`${thT} text-right`}>{fmtTrieu(totalsView.dsBan)}</th>
                <th className={`${thT} text-right`}>{fmtTrieu(totalsView.totalSalesYear)}</th>
                {displayMonths.map(m => (
                  <th key={m} className={`${thT} text-right`}>{fmtTrieu(totalsView[`m${m}Amount`])}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="border border-gray-200 px-2 py-2 bg-gray-50" colSpan={1}>
                      <div className="h-3 bg-gray-200 rounded w-24" />
                    </td>
                    <td className="border border-gray-200 px-2 py-2 bg-gray-50" colSpan={totalCols - 1}>
                      <div className="h-3 bg-gray-200 rounded w-full" />
                    </td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={totalCols} className="border border-gray-200 px-3 py-8 text-center text-gray-400 text-sm">
                    {debouncedSearch ? 'Không tìm thấy kết quả phù hợp.' : 'Chưa có F1 nào.'}
                  </td>
                </tr>
              ) : rows.map((item, idx) => {
                const stripeClass = idx % 2 === 1 ? 'bg-gray-50' : 'bg-white';
                // DS Bán = dsBan (BE mới, Σsubtotal); fallback total_amount lẻ+sỉ (field cũ) để hiện ngay cả khi BE chưa deploy.
                const dsBanVal = item.dsBan ?? ((Number(item.retailAmount) || 0) + (Number(item.wholesaleAmount) || 0));
                const hasSell = (Number(dsBanVal) || 0) > 0;
                return (
                  <tr key={item.id || idx} className="hover:bg-blue-50/40">
                    {/* Thành viên — sticky-left */}
                    <td className={`sticky left-0 z-20 ${stripeClass} border border-gray-300 px-2 py-1.5 align-top min-w-[120px]`}>
                      <div className="flex items-start gap-1">
                        <div className="min-w-0 flex-1">
                          <p className="font-bold text-blue-900 text-[12px] leading-tight">{item.code}</p>
                          <p className="text-[11px] text-gray-700 leading-tight truncate max-w-[88px]">{item.facebook || item.name || '-'}</p>
                        </div>
                        {/* Nhảy thẳng vào /f1 của F1 này (impersonate) để xem cấp dưới của họ */}
                        <button onClick={(e) => { e.stopPropagation(); impersonateF1(item); }}
                                disabled={impersonatingId === item.id}
                                title="Xem trang F1 của người này"
                                className="flex-shrink-0 w-6 h-6 rounded-md bg-indigo-50 text-indigo-600 hover:bg-indigo-100 flex items-center justify-center disabled:opacity-50">
                          <span className="material-symbols-rounded" style={{ fontSize: '15px' }}>{impersonatingId === item.id ? 'hourglass_top' : 'login'}</span>
                        </button>
                      </div>
                    </td>
                    {/* Quân số */}
                    <td className={`${stripeClass} border border-gray-300 px-1 py-1.5 text-center`}>
                      <button onClick={(e) => { e.stopPropagation(); setSelectedF2F1(item); }}
                              className="text-blue-600 font-bold underline decoration-blue-300 hover:text-blue-800">
                        {item.f2Count ?? 0}
                      </button>
                    </td>
                    <td className={`${stripeClass} border border-gray-300 px-1 py-1.5 text-center text-gray-800 font-medium`}>
                      {fmtInt(item.tongF)}
                    </td>
                    <td className={`${stripeClass} border border-gray-300 px-1 py-1.5 text-center`}>
                      {item.newF1Count > 0 ? (
                        <span className="text-emerald-700 font-bold">+{item.newF1Count}</span>
                      ) : <span className="text-gray-300">-</span>}
                    </td>
                    {/* Kho */}
                    <td className={`${stripeClass} border border-gray-300 px-1 py-1.5 text-center`}>
                      <button onClick={(e) => { e.stopPropagation(); setSelectedInventoryF1(item); }}
                              className="text-blue-600 font-bold underline decoration-blue-300 hover:text-blue-800">
                        {fmtInt(item.totalInventory)}
                      </button>
                    </td>
                    {/* Thi đua tháng */}
                    <td className={`${stripeClass} border border-gray-300 px-1 py-1.5 text-center text-violet-800`}>{fmtInt(item.dailyCount)}</td>
                    <td className={`${stripeClass} border border-gray-300 px-1 py-1.5 text-center text-violet-800`}>{fmtInt(item.meetingApproved)}</td>
                    <td className={`${stripeClass} border border-gray-300 px-1 py-1.5 text-center text-violet-800`}>{fmtInt(item.reelsApproved)}</td>
                    <td className={`${stripeClass} border border-gray-300 px-1 py-1.5 text-center text-violet-800`}>{fmtInt(item.trendApproved)}</td>
                    <td className={`${stripeClass} border border-gray-300 px-1 py-1.5 text-center text-violet-800`}>{fmtInt(item.liveApproved)}</td>
                    <td className={`${stripeClass} border border-gray-300 px-1 py-1.5 text-center text-violet-800`}>{fmtInt(item.marketApproved)}</td>
                    <td className={`${stripeClass} border border-gray-300 px-1 py-1.5 text-center text-violet-800`}>{fmtInt(item.spaApproved)}</td>
                    {/* Doanh số bán (tháng) */}
                    <td className={`${stripeClass} border border-gray-300 px-1 py-1.5 text-center text-gray-800`}>{fmtInt(item.retailOrders)}</td>
                    <td className={`${stripeClass} border border-gray-300 px-1 py-1.5 text-center text-gray-800`}>{fmtInt(item.wholesaleOrders)}</td>
                    {/* DS Bán = Σ order_details.subtotal (lẻ+sỉ), click → popup chi tiết SP × SL × đơn giá */}
                    <td onClick={() => hasSell && openSellDetail(item)}
                        className={`${stripeClass} border border-gray-300 px-1 py-1.5 text-right font-bold text-green-800 ${hasSell ? 'cursor-pointer hover:bg-green-50' : ''}`}>
                      {fmtTrieu(dsBanVal)}
                    </td>
                    {/* Doanh số nhập năm — Tổng năm trước, rồi T{m} có data */}
                    <td className={`${stripeClass} border border-gray-300 px-1 py-1.5 text-right font-bold text-yellow-900`}>
                      {fmtTrieu(item.totalSalesYear)}
                    </td>
                    {displayMonths.map(m => {
                      const amt = item[`m${m}Amount`] || item[`m${m}_amount`] || 0;
                      const status = item[`m${m}Status`];
                      const hasAmount = amt > 0;
                      return (
                        <td key={m}
                            onClick={() => hasAmount && openMonthDetail(item, m)}
                            className={`border border-gray-300 px-1 py-1.5 text-right text-[10.5px] font-medium ${cellStatusStyle(status, hasAmount)} ${hasAmount ? 'cursor-pointer' : ''}`}>
                          {hasAmount ? fmtTrieu(amt) : '-'}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Legend cho cell matrix */}
        <div className="px-2 py-1.5 flex items-center gap-2 text-[10px] text-gray-600 bg-white border-b border-gray-200 flex-wrap">
          <span className="flex items-center gap-1"><span className="w-3 h-3 inline-block rounded-sm bg-green-100 border border-green-300" /> Đã duyệt</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 inline-block rounded-sm bg-yellow-100 border border-yellow-300" /> Chờ duyệt</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 inline-block rounded-sm bg-red-100 border border-red-300" /> Từ chối</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 inline-block rounded-sm bg-gray-50 border border-gray-300" /> Chưa nộp</span>
          <span className="ml-auto italic">Tap ô tiền để xem chi tiết</span>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 py-2 bg-white border-t border-gray-200">
            <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}
                    className="px-3 py-1 text-xs rounded-md border border-gray-300 bg-white text-gray-700 disabled:opacity-40 hover:bg-gray-50">
              ← Trước
            </button>
            <span className="text-xs text-gray-600 px-2">{page} / {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    className="px-3 py-1 text-xs rounded-md border border-gray-300 bg-white text-gray-700 disabled:opacity-40 hover:bg-gray-50">
              Tiếp →
            </button>
          </div>
        )}
      </div>

      {/* Modal: Inventory Details */}
      {selectedInventoryF1 && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 animate-fade-in" onClick={() => setSelectedInventoryF1(null)}>
          <div className="bg-white rounded-3xl w-full max-w-sm overflow-hidden flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 bg-blue-50 border-b border-blue-100">
              <div>
                <h3 className="text-[16px] font-bold text-blue-950 leading-tight">Mặt hàng tồn kho</h3>
                <p className="text-[13px] text-blue-700 font-medium mt-0.5">{selectedInventoryF1.code}{selectedInventoryF1.code && (selectedInventoryF1.facebook || selectedInventoryF1.name) ? ' - ' : ''}{selectedInventoryF1.facebook || selectedInventoryF1.name}</p>
              </div>
              <button onClick={() => setSelectedInventoryF1(null)} className="w-8 h-8 rounded-full bg-blue-200/50 flex items-center justify-center text-blue-800 hover:bg-blue-300">
                <span className="material-symbols-rounded" style={{ fontSize: '20px' }}>close</span>
              </button>
            </div>
            <div className="p-5 max-h-[60vh] overflow-y-auto">
              {selectedInventoryF1.inventoryDetails && selectedInventoryF1.inventoryDetails.length > 0 ? (
                <div className="space-y-3">
                  {selectedInventoryF1.inventoryDetails.map((detail, idx) => (
                    <div key={idx} className="flex justify-between items-center pb-3 border-b border-gray-100 last:border-0 last:pb-0">
                      <div className="flex items-center gap-2">
                        <span className="material-symbols-rounded text-indigo-400" style={{ fontSize: '18px' }}>inventory_2</span>
                        <span className="text-[14px] text-gray-800 font-medium">{detail.productName}</span>
                      </div>
                      <span className="text-[14px] font-bold text-gray-900 bg-gray-100/80 px-2.5 py-1 rounded-lg">
                        {detail.quantity.toLocaleString('vi-VN')}
                      </span>
                    </div>
                  ))}
                  <div className="pt-3 mt-1 border-t-2 border-dashed border-gray-200 flex justify-between items-center">
                    <span className="text-[15px] font-bold text-gray-800">Tổng sản phẩm:</span>
                    <span className="text-lg font-black text-blue-700">{(selectedInventoryF1.totalInventory || 0).toLocaleString('vi-VN')}</span>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-6 text-gray-500">
                  <span className="material-symbols-rounded text-4xl text-gray-300 mb-2">inventory_2</span>
                  <p className="text-[14px]">Không có sản phẩm nào trong kho.</p>
                </div>
              )}
            </div>
            <div className="p-3 bg-gray-50/80 border-t border-gray-100 flex justify-end">
              <button onClick={() => setSelectedInventoryF1(null)} className="px-6 py-2 bg-white border shadow-sm border-gray-300 rounded-xl text-sm font-bold text-gray-700 hover:bg-gray-100">Đóng</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: F2 Sub-members */}
      {selectedF2F1 && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 animate-fade-in" onClick={() => setSelectedF2F1(null)}>
          <div className="bg-white rounded-3xl w-full max-w-sm overflow-hidden flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 bg-indigo-50 border-b border-indigo-100">
              <div>
                <h3 className="text-[16px] font-bold text-indigo-950 leading-tight">Danh sách F1 của</h3>
                <p className="text-[13px] text-indigo-700 font-medium mt-0.5">{selectedF2F1.code}{selectedF2F1.code && (selectedF2F1.facebook || selectedF2F1.name) ? ' - ' : ''}{selectedF2F1.facebook || selectedF2F1.name}</p>
              </div>
              <button onClick={() => setSelectedF2F1(null)} className="w-8 h-8 rounded-full bg-indigo-200/50 flex items-center justify-center text-indigo-800 hover:bg-indigo-300">
                <span className="material-symbols-rounded" style={{ fontSize: '20px' }}>close</span>
              </button>
            </div>
            <div className="p-5 max-h-[60vh] overflow-y-auto">
              {selectedF2F1.f2Members && selectedF2F1.f2Members.length > 0 ? (
                <div className="space-y-2">
                  {selectedF2F1.f2Members.map((member, idx) => (
                    <div key={idx} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                      <div>
                        <p className="text-[13px] font-bold text-blue-900">{member.code}{member.code && (member.facebook || member.name) ? ' - ' : ''}{member.facebook || member.name}</p>
                        <p className="text-[11px] text-gray-500">Tham gia: {formatDate(member.createdAt)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-6 text-gray-500">
                  <span className="material-symbols-rounded text-4xl text-gray-300 mb-2">group</span>
                  <p className="text-[14px]">Chưa có F1 nào.</p>
                </div>
              )}
            </div>
            <div className="p-3 bg-gray-50/80 border-t border-gray-100 flex justify-end">
              <button onClick={() => setSelectedF2F1(null)} className="px-6 py-2 bg-white border shadow-sm border-gray-300 rounded-xl text-sm font-bold text-gray-700 hover:bg-gray-100">Đóng</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: chi tiết doanh số — dùng chung cho 'nhap' (sale_reports) và 'ban' (order_details) */}
      {saleDetail && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 animate-fade-in" onClick={() => setSaleDetail(null)}>
          <div className="bg-white rounded-3xl w-full max-w-sm overflow-hidden flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className={`flex items-center justify-between px-3 py-2 border-b ${
              saleDetail.kind === 'ban' ? 'bg-green-50 border-green-100' :
              saleDetail.status === 'approved' ? 'bg-green-50 border-green-100' :
              saleDetail.status === 'pending' ? 'bg-yellow-50 border-yellow-100' :
              saleDetail.status === 'rejected' ? 'bg-red-50 border-red-100' :
              'bg-gray-50 border-gray-100'
            }`}>
              <div>
                <h3 className="text-[15px] font-bold leading-tight">
                  {saleDetail.kind === 'ban' ? 'Doanh số bán' : 'Doanh số nhập'} T{saleDetail.month}/{selectedYear}
                </h3>
                <p className="text-[12px] font-medium mt-0.5 text-gray-700">
                  {saleDetail.row?.code}{saleDetail.row?.code && (saleDetail.row?.facebook || saleDetail.row?.name) ? ' - ' : ''}{saleDetail.row?.facebook || saleDetail.row?.name}
                </p>
                {saleDetail.kind !== 'ban' && (
                  <p className="text-[11px] mt-0.5">
                    {saleDetail.status === 'approved' && <span className="text-green-700 font-semibold">✓ Đã duyệt</span>}
                    {saleDetail.status === 'pending' && <span className="text-yellow-700 font-semibold">⏳ Chờ duyệt</span>}
                    {saleDetail.status === 'rejected' && <span className="text-red-700 font-semibold">✗ Bị từ chối</span>}
                    {!saleDetail.status && <span className="text-gray-500">Chưa nộp duyệt</span>}
                  </p>
                )}
              </div>
              <button onClick={() => setSaleDetail(null)} className="w-8 h-8 rounded-full bg-white/70 flex items-center justify-center text-gray-700 hover:bg-white">
                <span className="material-symbols-rounded" style={{ fontSize: '20px' }}>close</span>
              </button>
            </div>
            <div className="p-2 max-h-[65vh] overflow-y-auto">
              {saleDetail.loading ? (
                <div className="py-4 text-center text-gray-400 text-xs">Đang tải...</div>
              ) : saleDetail.denied ? (
                <div className="py-4 text-center text-red-500 text-xs">Không có quyền xem chi tiết F1 này.</div>
              ) : saleDetail.items && saleDetail.items.length > 0 ? (
                <div>
                  {(() => {
                    const sum = (arr) => arr.reduce((s, x) => s + (Number(x.amount) || 0), 0);
                    // Bảng compact dùng chung: Sản phẩm (name_short) | SL | Giá | Tiền — pad cực nhỏ.
                    const tbl = (rows) => (
                      <table className="w-full border-collapse text-[11px]">
                        <thead>
                          <tr className="text-gray-400 border-b border-gray-200">
                            <th className="px-1 py-0.5 text-left font-medium">Sản phẩm</th>
                            <th className="px-1 py-0.5 text-right font-medium">SL</th>
                            <th className="px-1 py-0.5 text-right font-medium">Giá</th>
                            <th className="px-1 py-0.5 text-right font-medium">Tiền</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((it, i) => (
                            <tr key={it.id ?? i} className="border-b border-gray-100">
                              <td className="px-1 py-0.5 text-gray-800 leading-tight">{it.productName || '-'}</td>
                              <td className="px-1 py-0.5 text-right text-gray-700 whitespace-nowrap">{fmtInt(it.count)}</td>
                              <td className="px-1 py-0.5 text-right text-gray-500 whitespace-nowrap">{fmtInt(it.price)}</td>
                              <td className="px-1 py-0.5 text-right font-semibold text-emerald-700 whitespace-nowrap">{fmtInt(it.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                    if (saleDetail.kind === 'ban') {
                      const le = saleDetail.items.filter(x => Number(x.type) === 1);
                      const si = saleDetail.items.filter(x => Number(x.type) === 2);
                      return (
                        <>
                          {le.length > 0 && (
                            <div className="mb-1.5">
                              <div className="flex justify-between items-center px-1 py-0.5 bg-blue-50 border-y border-blue-100">
                                <span className="text-[11px] font-bold text-blue-800">Lẻ</span>
                                <span className="text-[11px] font-bold text-blue-800">{fmtVnd(sum(le))}</span>
                              </div>
                              {tbl(le)}
                            </div>
                          )}
                          {si.length > 0 && (
                            <div className="mb-1.5">
                              <div className="flex justify-between items-center px-1 py-0.5 bg-amber-50 border-y border-amber-100">
                                <span className="text-[11px] font-bold text-amber-800">Sỉ</span>
                                <span className="text-[11px] font-bold text-amber-800">{fmtVnd(sum(si))}</span>
                              </div>
                              {tbl(si)}
                            </div>
                          )}
                        </>
                      );
                    }
                    return tbl(saleDetail.items);
                  })()}
                  <div className="mt-1 pt-1 border-t-2 border-gray-300 flex justify-between items-center px-1">
                    <span className="text-[12px] font-bold text-gray-800">Tổng{saleDetail.kind === 'ban' ? ' DS bán' : ''}</span>
                    <span className="text-[13px] font-black text-amber-700">{fmtVnd(saleDetail.total)}</span>
                  </div>
                  {saleDetail.submission?.approvalNotes && (
                    <div className="mt-1.5 p-1.5 bg-orange-50 border border-orange-200 rounded">
                      <p className="text-[10px] font-semibold text-orange-800">Ghi chú duyệt:</p>
                      <p className="text-[11px] text-orange-900">{saleDetail.submission.approvalNotes}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="py-4 text-center text-gray-400 text-xs">Chưa có chi tiết sản phẩm.</div>
              )}
            </div>
            <div className="p-2 bg-gray-50/80 border-t border-gray-100 flex justify-end">
              <button onClick={() => setSaleDetail(null)} className="px-4 py-1.5 bg-white border shadow-sm border-gray-300 rounded-lg text-xs font-bold text-gray-700 hover:bg-gray-100">Đóng</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default F1List;
